const clean = (v) => (v == null ? '' : String(v).trim())

const hasValue = (obj = {}) => Object.values(obj).some((v) => clean(v) !== '')

const GUARDIAN_LABEL_RE =
  /\b(parent|guardian|mother|father|mom|dad|step[- ]?(parent|mother|father|mom|dad))\b/i

export function isContactYesFlag(value) {
  if (value === true) return true
  if (value === false) return false
  const s = String(value || '')
    .trim()
    .toLowerCase()
  return s === 'y' || s === 'yes' || s === 'true' || s === '1'
}

function optionalFlag(value) {
  if (value === true || value === false) return value
  if (value == null || clean(value) === '') return undefined
  return isContactYesFlag(value)
}

export function looksLikeGuardian(relationship, recordType) {
  return GUARDIAN_LABEL_RE.test(`${clean(relationship)} ${clean(recordType)}`)
}

export function preferredContactPhone(c = {}) {
  return clean(c.cellPhone) || clean(c.homePhone) || clean(c.workPhone) || clean(c.phone)
}

/**
 * Display-only US phone format. Stores stay as imported (often 10 digits).
 * 10 digits → xxx-xxx-xxxx; 11 digits starting with 1 → same last-10 format.
 * Other values are returned unchanged so extensions / international stay intact.
 */
export function formatContactPhone(value) {
  const raw = clean(value)
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  const local =
    digits.length === 10
      ? digits
      : digits.length === 11 && digits.startsWith('1')
        ? digits.slice(1)
        : ''
  if (!local) return raw
  return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`
}

export function contactHasValue(c = {}) {
  return Boolean(
    clean(c.name) ||
    clean(c.firstName) ||
    clean(c.lastName) ||
    preferredContactPhone(c) ||
    clean(c.email)
  )
}

/**
 * Canonical sparse contact. Blank strings are omitted so the Contact dialog
 * can render only fields Aeries actually sent.
 */
export function sparseContact(c = {}, fallbackOrder) {
  const firstName = clean(c.firstName)
  const lastName = clean(c.lastName)
  const name = clean(c.name) || [firstName, lastName].filter(Boolean).join(' ')
  const cellPhone = clean(c.cellPhone)
  const homePhone = clean(c.homePhone)
  const workPhone = clean(c.workPhone)
  const phone = preferredContactPhone({
    cellPhone,
    homePhone,
    workPhone,
    phone: c.phone
  })
  const email = clean(c.email).toLowerCase()
  const relationship = clean(c.relationship)
  const recordType = clean(c.recordType)
  const orderRaw = c.order
  let order = null
  if (Number.isFinite(Number(orderRaw)) && clean(orderRaw) !== '') {
    order = Number(orderRaw)
  } else if (Number.isFinite(fallbackOrder)) {
    order = fallbackOrder
  }

  const out = {}
  if (name) out.name = name
  if (firstName) out.firstName = firstName
  if (lastName) out.lastName = lastName
  if (relationship) out.relationship = relationship
  if (recordType) out.recordType = recordType
  if (cellPhone) out.cellPhone = cellPhone
  if (homePhone) out.homePhone = homePhone
  if (workPhone) out.workPhone = workPhone
  if (phone) out.phone = phone
  if (email) out.email = email
  if (order != null) out.order = order

  const primary = optionalFlag(c.primary)
  if (primary === true) out.primary = true
  else if (primary === false) out.primary = false

  const livesWith = optionalFlag(c.livesWith)
  if (livesWith === true) out.livesWith = true
  else if (livesWith === false) out.livesWith = false

  return out
}

export function rankContacts(a = {}, b = {}) {
  const ap = a.primary === true
  const bp = b.primary === true
  if (ap !== bp) return ap ? -1 : 1
  const al = a.livesWith === true
  const bl = b.livesWith === true
  if (al !== bl) return al ? -1 : 1
  const ag = a.guardian === true || looksLikeGuardian(a.relationship, a.recordType)
  const bg = b.guardian === true || looksLikeGuardian(b.relationship, b.recordType)
  if (ag !== bg) return ag ? -1 : 1
  if (a.order == null && b.order != null) return 1
  if (b.order == null && a.order != null) return -1
  if (a.order != null && b.order != null && a.order !== b.order) return a.order - b.order
  return 0
}

function withLegacyPrimary(list) {
  const hasFlag = list.some((c) => c.primary === true || c.primary === false)
  if (hasFlag || !list.length) return list
  return list.map((c, i) => (i === 0 ? { ...c, primary: true } : c))
}

function parseContactList(list) {
  return list
    .map((c, i) => sparseContact(c, i + 1))
    .filter(contactHasValue)
    .sort(rankContacts)
}

export function storedContactsFromPrivateDoc(privateDoc) {
  if (!privateDoc) return []
  if (Array.isArray(privateDoc.guardianContacts) && privateDoc.guardianContacts.length) {
    return parseContactList(privateDoc.guardianContacts)
  }
  const legacy = privateDoc.legacyGuardian || {}
  return parseContactList([
    {
      name: legacy.name,
      phone: legacy.phone,
      email: legacy.email,
      order: 1
    }
  ])
}

export function getContactsFromPrivateDoc(privateDoc) {
  return withLegacyPrimary(storedContactsFromPrivateDoc(privateDoc))
}

function firstNonBlank(...values) {
  for (const value of values) {
    if (clean(value) !== '') return clean(value)
  }
  return ''
}

/**
 * Build the studentPrivate payload from imported source-field bags. Returns null
 * when the import carried no contact/address PII so callers can skip the write.
 */
export function buildStudentPrivatePayload({
  studentId,
  schoolId = '',
  aeries = {},
  seis = {}
} = {}) {
  if (!studentId) return null

  const payload = {
    studentId,
    updatedAt: new Date().toISOString()
  }
  if (schoolId) payload.schoolId = schoolId

  if (Array.isArray(aeries.contacts) && aeries.contacts.length) {
    const contacts = aeries.contacts.map((c, i) => sparseContact(c, i + 1)).filter(contactHasValue)
    if (contacts.length) payload.guardianContacts = contacts
  }

  const legacyGuardian = {
    name: firstNonBlank(aeries.parentName, seis.parentName),
    phone: firstNonBlank(aeries.parentPhone, seis.parentPhone),
    email: firstNonBlank(aeries.parentEmail, seis.parentEmail).toLowerCase()
  }
  if (hasValue(legacyGuardian)) payload.legacyGuardian = legacyGuardian

  const studentContact = {
    phone: firstNonBlank(aeries.phone, seis.phone),
    email: firstNonBlank(aeries.email, seis.email).toLowerCase()
  }
  if (hasValue(studentContact)) payload.studentContact = studentContact

  const mailingAddress = {
    street: firstNonBlank(aeries.address, seis.address),
    city: firstNonBlank(aeries.city, seis.city),
    state: firstNonBlank(aeries.state, seis.state),
    zipCode: firstNonBlank(aeries.zipCode, seis.zipCode)
  }
  if (hasValue(mailingAddress)) payload.mailingAddress = mailingAddress

  const sources = {
    ...(Object.keys(aeries).length ? { aeries: true } : {}),
    ...(Object.keys(seis).length ? { seis: true } : {})
  }
  if (Object.keys(sources).length) payload.sources = sources

  const hasPrivateContent =
    payload.guardianContacts?.length ||
    hasValue(payload.legacyGuardian || {}) ||
    hasValue(payload.studentContact || {}) ||
    hasValue(payload.mailingAddress || {})

  return hasPrivateContent ? payload : null
}
